import React, { useRef } from 'react';
import {
    NewTableStyledThead,
    NewTableStyledTrHeader,
    NewTableStyledTh,
    NewTableCol,
    NewTableColGroup,
    NewTableWrapper,
    TABLE_CHECKBOX_COLUMN_WIDTH,
    NewTableStyledTableDiv,
    NewTableStyledTableTable
} from './HBNewTable.styles';
import NewTableRowList from './HBNewTableRowList';
import NewTableSelectableHeader from './HBNewTableSelectableHeader';
import { INewTableProps, INewTableColumn } from './HBNewTable.types';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import styled from 'styled-components';
import HBNewTableNoResults from './HBNewTableNoResults';
import HBNewTableExpandedIndicator from './HBNewTableExpandedIndicator';
import { useDetectScroll } from '../../hooks/useDetectScroll';
import HBNewTableRowCheckbox from './HBNewTableRowCheckbox';

const CHECKBOX_COLUMN_ID = 'row-checkbox';

/**
 * @deprecated HBNewTable is the legacy table component and should not be used for new work. Despite the "New" in its name, this component predates HBTable by several years.
 */
const HBNewTable = <T,>({
    items,
    columnDefinitions,
    getItemId,
    selectableProps,
    infiniteScrollProps,
    expandableProps,
    pinProps,
    hasSpacer,
    testId,
    testIdName,
    tourId,
    ariaLabel,
    maxHeight,
    noResultsProps,
    useNativeTable
}: INewTableProps<T> & MustIncludeOneOfBasicTestIdProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'table',
        componentName: 'HBNewTable'
    });

    const isSelectable = !!selectableProps;
    const isSelectableMode = selectableProps?.selectedItemIds?.length > 0;
    const hideTableHeader =
        !selectableProps?.showTableHeaderOnSelect &&
        selectableProps?.selectedItemIds?.length > 0;

    const TableComponent = useNativeTable
        ? NewTableStyledTableTable
        : NewTableStyledTableDiv;

    const noResultsJsx = () =>
        noResultsProps && (
            <HBNewTableNoResults
                title={noResultsProps.title}
                subtitle={noResultsProps.subtitle}
                columnsCount={columnDefinitions.length}
            />
        );

    const enrichedColumnDefinitions: INewTableColumn<T>[] = [
        ...(selectableProps
            ? [
                  {
                      id: CHECKBOX_COLUMN_ID,
                      renderHeader: () => (
                          <HBNewTableRowCheckbox
                              item={undefined}
                              itemId="select-all"
                              isItemSelected={selectableProps?.allItemsSelected}
                              onSelectItem={(_, isSelected) => {
                                  if (isSelected) {
                                      selectableProps.selectAll();
                                  } else {
                                      selectableProps.clearSelections();
                                  }
                              }}
                              testIdName="select-all"
                          />
                      ),
                      renderCell: (item: T) => {
                          const itemId = getItemId(item);
                          return (
                              <HBNewTableRowCheckbox
                                  item={item}
                                  isItemSelected={selectableProps.selectedItemIds.includes(
                                      itemId
                                  )}
                                  itemId={itemId}
                                  onSelectItem={selectableProps.onSelectItem}
                              />
                          );
                      },
                      width: `${TABLE_CHECKBOX_COLUMN_WIDTH}px`
                  }
              ]
            : []),
        ...columnDefinitions,
        ...(expandableProps?.shouldShowExpandColumn
            ? [
                  {
                      id: 'expandableIndicator',
                      renderCell: (_, { isRowExpanded }) => (
                          <HBNewTableExpandedIndicator
                              isRowExpanded={isRowExpanded}
                          />
                      ),
                      width: '46px'
                  }
              ]
            : [])
    ];

    const ref = useRef<HTMLDivElement>(null);

    const { isScrolled, isScrolledToEnd } = useDetectScroll({
        elementRef: ref,
        direction: 'horizontal'
    });

    return (
        <Container>
            {isSelectableMode && (
                <NewTableSelectableHeader selectableProps={selectableProps} />
            )}
            <NewTableWrapper
                {...pinProps}
                ref={ref}
                $isSelectable={isSelectable}
                $isScrolled={isScrolled}
                $isScrolledToEnd={isScrolledToEnd}
                $maxHeight={maxHeight}
            >
                <TableComponent
                    data-testid={finalTestId}
                    data-tour-id={tourId}
                    role="table"
                    aria-label={ariaLabel}
                >
                    <NewTableColGroup>
                        {enrichedColumnDefinitions.map(column => (
                            <NewTableCol
                                key={column.id}
                                $width={column.width}
                            />
                        ))}
                    </NewTableColGroup>
                    <NewTableStyledThead>
                        <NewTableStyledTrHeader
                            hasSpacer={hasSpacer}
                            $isSelectable={isSelectable}
                        >
                            {enrichedColumnDefinitions.map(column => {
                                const shouldHide =
                                    column.id !== CHECKBOX_COLUMN_ID &&
                                    hideTableHeader;
                                const component = column.renderHeader
                                    ? column.renderHeader({ column })
                                    : null;
                                return (
                                    <NewTableStyledTh
                                        key={column.id}
                                        hide={shouldHide}
                                    >
                                        {component}
                                    </NewTableStyledTh>
                                );
                            })}
                        </NewTableStyledTrHeader>
                    </NewTableStyledThead>
                    {items.length > 0 ? (
                        <NewTableRowList
                            items={items}
                            columnDefinitions={enrichedColumnDefinitions}
                            getItemId={getItemId}
                            selectableProps={selectableProps}
                            infiniteScrollProps={infiniteScrollProps}
                            expandableProps={expandableProps}
                            hasSpacer={hasSpacer}
                        />
                    ) : (
                        noResultsJsx()
                    )}
                </TableComponent>
            </NewTableWrapper>
        </Container>
    );
};

export default HBNewTable;

// -------------------------------- Styles -------------------------------- //

const Container = styled.div`
    position: relative;
`;
